/**
 * Appointments Page - Claude.ai-inspired Design
 * Enhanced with date range picker, detail view, bulk status, FR-026 badges (T064-T068)
 */

import { useState } from 'react';
import {
  Calendar,
  Search,
  Filter,
  Clock,
  User,
  MapPin,
  Video,
  Plus,
  ChevronRight,
  CheckSquare,
  Square,
} from 'lucide-react';
import { useAppointments, useBulkUpdateStatus } from '../hooks/useAppointments';
import { TableListSkeleton } from '../components/ui/SkeletonLoaders';
import { useToast } from '../components/ui';
import AppointmentDetail, {
  AppointmentStatusBadge,
} from '../components/Appointments/AppointmentDetail';

export default function AppointmentsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [page, setPage] = useState(1);
  const { showToast } = useToast();

  const { data, isLoading, error } = useAppointments({
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    startDate: startDate ? new Date(startDate).toISOString() : undefined,
    endDate: endDate ? new Date(endDate).toISOString() : undefined,
    page,
    limit: 20,
  });

  const bulkUpdate = useBulkUpdateStatus();

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data?.appointments) {
      return;
    }
    if (selectedIds.size === data.appointments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.appointments.map((a) => a.id)));
    }
  };

  const handleBulkAction = () => {
    if (!bulkStatus || selectedIds.size === 0) {
      return;
    }
    bulkUpdate.mutate(
      { appointmentIds: Array.from(selectedIds), status: bulkStatus },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          setBulkStatus('');
          showToast('success', `${selectedIds.size} appointment(s) updated`);
        },
        onError: () => {
          showToast('error', 'Failed to update appointments');
        },
      }
    );
  };

  if (isLoading) {
    return <TableListSkeleton columns={7} rows={8} title="appointments" />;
  }

  if (error) {
    return (
      <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
        <div className="flex items-center gap-3 text-destructive">
          <Calendar className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Failed to load appointments</p>
            <p className="text-sm mt-1 opacity-90">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  const appointments = data?.appointments || [];
  const stats = {
    total: data?.pagination?.total || appointments.length,
    today: appointments.filter((a) => {
      const today = new Date().toDateString();
      return new Date(a.startTime).toDateString() === today;
    }).length,
    upcoming: appointments.filter(
      (a) => new Date(a.startTime) > new Date() && a.status !== 'CANCELLED'
    ).length,
    completed: appointments.filter((a) => a.status === 'COMPLETED').length,
  };

  return (
    <div className="space-y-8 pb-8">
      {/* Appointment Detail Modal */}
      {selectedId && (
        <AppointmentDetail appointmentId={selectedId} onClose={() => setSelectedId(null)} />
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h1>Appointments</h1>
          <p className="text-muted-foreground text-lg">Manage your appointments and schedule</p>
        </div>
        <button className="claude-button claude-button-primary">
          <Plus className="w-4 h-4" />
          New Appointment
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="claude-card p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="claude-card p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Today</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.today}</p>
            </div>
          </div>
        </div>

        <div className="claude-card p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <ChevronRight className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Upcoming</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.upcoming}</p>
            </div>
          </div>
        </div>

        <div className="claude-card p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Completed</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.completed}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="claude-card p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder="Search by customer name or title..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="claude-input pl-10 w-full"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`claude-button ${showFilters ? 'claude-button-primary' : 'claude-button-secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-border">
            {/* Status Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="claude-input w-full"
              >
                <option value="all">All Statuses</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="NO_SHOW">No Show</option>
              </select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                className="claude-input w-full"
              />
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                className="claude-input w-full"
              />
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions (T068) */}
      {selectedIds.size > 0 && (
        <div className="claude-card p-4 flex items-center gap-4 bg-primary/5">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="claude-input text-sm"
          >
            <option value="">Change status to...</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="NO_SHOW">No Show</option>
          </select>
          <button
            onClick={handleBulkAction}
            disabled={!bulkStatus || bulkUpdate.isPending}
            className="claude-button claude-button-primary text-sm"
          >
            {bulkUpdate.isPending ? 'Updating...' : 'Apply'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="claude-button claude-button-secondary text-sm"
          >
            Clear
          </button>
        </div>
      )}

      {/* Appointments Table */}
      <div className="claude-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="claude-table">
            <thead>
              <tr>
                <th className="w-10">
                  <button onClick={toggleSelectAll} className="p-1">
                    {appointments.length > 0 && selectedIds.size === appointments.length ? (
                      <CheckSquare className="w-4 h-4 text-primary" />
                    ) : (
                      <Square className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </th>
                <th>Customer</th>
                <th>Appointment</th>
                <th>Date & Time</th>
                <th>Duration</th>
                <th>Location</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {appointments.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="claude-empty-state">
                      <Calendar className="claude-empty-state-icon" />
                      <h3 className="claude-empty-state-title">No appointments found</h3>
                      <p className="claude-empty-state-description">
                        {searchTerm || statusFilter !== 'all' || startDate || endDate
                          ? 'Try adjusting your filters or search term'
                          : 'Create your first appointment to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                appointments.map((appointment) => (
                  <tr key={appointment.id} className="cursor-pointer">
                    <td onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleSelect(appointment.id)} className="p-1">
                        {selectedIds.has(appointment.id) ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4 text-muted-foreground" />
                        )}
                      </button>
                    </td>
                    <td onClick={() => setSelectedId(appointment.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{appointment.customerName}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {appointment.customerEmail}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td onClick={() => setSelectedId(appointment.id)}>
                      <div>
                        <p className="font-medium">{appointment.title}</p>
                        {appointment.description && (
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {appointment.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td onClick={() => setSelectedId(appointment.id)}>
                      <div className="space-y-1">
                        <p className="font-medium">
                          {new Date(appointment.startTime).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(appointment.startTime).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </td>
                    <td onClick={() => setSelectedId(appointment.id)}>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>{appointment.duration} min</span>
                      </div>
                    </td>
                    <td onClick={() => setSelectedId(appointment.id)}>
                      <div className="space-y-1">
                        {appointment.meetingUrl ? (
                          <div className="flex items-center gap-2">
                            <Video className="w-4 h-4 text-primary" />
                            <span className="text-sm">Video Call</span>
                          </div>
                        ) : appointment.location ? (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{appointment.location}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Not specified</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <AppointmentStatusBadge
                        status={appointment.status}
                        startTime={appointment.startTime}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {appointments.length} of {data.pagination.total} appointments
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="claude-button claude-button-secondary"
            >
              Previous
            </button>
            <span className="flex items-center px-3 text-sm text-muted-foreground">
              Page {page} of {data.pagination.pages}
            </span>
            <button
              disabled={page === data.pagination.pages}
              onClick={() => setPage((p) => p + 1)}
              className="claude-button claude-button-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
